import { useState, useMemo } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import SearchIcon from '@material-ui/icons/Search';
import WarningIcon from '@material-ui/icons/Warning';
import { CHOREO_LABELS } from '@openchoreo/backstage-plugin-common';
import { useStyles } from './styles';
import { SCOPE_CLUSTER, type BindingScope } from '../constants';
import { useActions } from '../hooks';
import {
  normalizeActions,
  getActionDisplayLabel,
  expandWildcards,
} from './actionUtils';

interface RoleRow {
  name: string;
  actions: string[];
  description?: string;
  labels?: Record<string, string>;
}

export interface BindingSummary {
  name: string;
  entitlement: { claim: string; value: string };
  effect: string;
  type: BindingScope;
  namespace?: string;
}

interface RolesTableProps {
  roles: RoleRow[];
  scope: BindingScope;
  scopeLabel: string;
  canUpdate: boolean;
  canDelete: boolean;
  updateDeniedTooltip: string;
  deleteDeniedTooltip: string;
  onEdit: (role: RoleRow) => void;
  onDelete: (name: string) => Promise<void>;
  onCheckBindings: (name: string) => Promise<BindingSummary[]>;
}

export const RolesTable = ({
  roles,
  scope,
  scopeLabel,
  canUpdate,
  canDelete,
  updateDeniedTooltip,
  deleteDeniedTooltip,
  onEdit,
  onDelete,
  onCheckBindings,
}: RolesTableProps) => {
  const classes = useStyles();
  const { actions: availableActionInfos } = useActions();
  const availableActions = useMemo(
    () => availableActionInfos.map(a => a.name),
    [availableActionInfos],
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  const [activeBindings, setActiveBindings] = useState<BindingSummary[]>([]);
  const [checkingBindings, setCheckingBindings] = useState(false);

  const filteredRoles = useMemo(() => {
    if (!searchQuery) return roles;
    const query = searchQuery.toLowerCase();
    return roles.filter(
      role =>
        role.name.toLowerCase().includes(query) ||
        role.actions.some(action => action.toLowerCase().includes(query)),
    );
  }, [roles, searchQuery]);

  const handleDeleteRole = async (name: string) => {
    setRoleToDelete(name);
    setCheckingBindings(true);
    setDeleteConfirmOpen(true);

    try {
      const bindings = await onCheckBindings(name);
      setActiveBindings(bindings);
    } catch {
      setActiveBindings([]);
    } finally {
      setCheckingBindings(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteConfirmOpen(false);
    setRoleToDelete(null);
    setActiveBindings([]);
  };

  const confirmDeleteRole = async () => {
    if (!roleToDelete) return;
    await onDelete(roleToDelete);
    closeDeleteDialog();
  };

  return (
    <>
      <TextField
        className={classes.searchField}
        placeholder={`Search ${scopeLabel.toLowerCase()}s...`}
        variant="outlined"
        size="small"
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {filteredRoles.length === 0 ? (
        <Box className={classes.emptyState}>
          <Typography variant="body1" color="textSecondary">
            {searchQuery
              ? `No ${scopeLabel.toLowerCase()}s match your search`
              : `No ${scopeLabel.toLowerCase()}s defined yet. Create your first ${scopeLabel.toLowerCase()} to get started.`}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell scope="col">Role Name</TableCell>
                <TableCell scope="col" className={classes.actionsCell}>
                  Actions
                </TableCell>
                <TableCell scope="col" align="right">
                  Operations
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoles.map(role => {
                const displayActions = normalizeActions(
                  role.actions,
                  availableActions,
                );
                const visibleActions = displayActions.slice(0, 5);
                const hiddenActionCount =
                  availableActions.length === 0
                    ? 0
                    : expandWildcards(displayActions, availableActions).size -
                      expandWildcards(visibleActions, availableActions).size;
                return (
                  <TableRow key={role.name}>
                    <TableCell>
                      <Typography variant="body1">
                        {role.name}
                        {role.labels?.[CHOREO_LABELS.SYSTEM] === 'true' && (
                          <Chip
                            label="System"
                            size="small"
                            variant="outlined"
                            style={{
                              marginLeft: 8,
                              fontSize: '0.7rem',
                              height: 20,
                            }}
                          />
                        )}
                      </Typography>
                      {role.description && (
                        <Typography variant="body2" color="textSecondary">
                          {role.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell className={classes.actionsCell}>
                      {displayActions.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                          No actions
                        </Typography>
                      ) : (
                        visibleActions.map(action => (
                          <Chip
                            key={action}
                            label={getActionDisplayLabel(action)}
                            size="small"
                            className={classes.actionsChip}
                          />
                        ))
                      )}
                      {hiddenActionCount > 0 && (
                        <Chip
                          label={`+${hiddenActionCount} more`}
                          size="small"
                          variant="outlined"
                          className={classes.actionsChip}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={updateDeniedTooltip || 'Edit'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onEdit(role)}
                            disabled={!canUpdate}
                            color="primary"
                            aria-label={`Edit role ${role.name}`}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={deleteDeniedTooltip || 'Delete'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRole(role.name)}
                            disabled={!canDelete}
                            className={classes.deleteIconButton}
                            aria-label={`Delete role ${role.name}`}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="roles-delete-dialog-title"
      >
        {checkingBindings && (
          <>
            <DialogTitle disableTypography id="roles-delete-dialog-title">
              <Typography variant="h6">Checking {scopeLabel} Usage</Typography>
            </DialogTitle>
            <DialogContent>
              <Box
                display="flex"
                alignItems="center"
                style={{ gap: 16 }}
                py={2}
                role="status"
                aria-busy="true"
              >
                <CircularProgress size={24} aria-hidden="true" />
                <Typography>Checking for role bindings...</Typography>
              </Box>
            </DialogContent>
          </>
        )}
        {!checkingBindings && activeBindings.length > 0 && (
          <>
            <DialogTitle disableTypography id="roles-delete-dialog-title">
              <Box className={classes.warningHeader}>
                <WarningIcon className={classes.warningIcon} />
                <Typography variant="h4" component="span">
                  Cannot Delete {scopeLabel}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                This role is referenced by the following binding
                {activeBindings.length > 1 ? 's' : ''}. Remove the role from
                these bindings to proceed with deletion.
              </DialogContentText>
              <List className={classes.mappingsList} dense>
                {activeBindings.map((binding, index) => {
                  let secondary: string | undefined;
                  if (scope === SCOPE_CLUSTER) {
                    secondary =
                      binding.type === SCOPE_CLUSTER
                        ? 'Type: Cluster'
                        : `Type: Namespace (${binding.namespace})`;
                  }
                  return (
                    <ListItem key={binding.name ?? index}>
                      <ListItemText
                        primary={`Binding: ${binding.name}`}
                        secondary={secondary}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDeleteDialog} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        )}
        {!checkingBindings && activeBindings.length === 0 && (
          <>
            <DialogTitle disableTypography id="roles-delete-dialog-title">
              <Typography variant="h4">Delete {scopeLabel}</Typography>
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete the {scopeLabel.toLowerCase()} "
                {roleToDelete}"?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDeleteDialog} variant="contained">
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteRole}
                variant="outlined"
                className={classes.deleteButton}
              >
                Delete
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};
